import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate unique IDs
const generateId = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 12; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result + Date.now().toString(36);
};

// Get or create visitor ID (persisted in localStorage)
const getVisitorId = () => {
  let visitorId = localStorage.getItem('simnet_visitor_id');
  if (!visitorId) {
    visitorId = generateId();
    localStorage.setItem('simnet_visitor_id', visitorId);
  }
  return visitorId;
};

// Get or create session ID (persisted in sessionStorage)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('simnet_session_id');
  if (!sessionId) {
    sessionId = generateId();
    sessionStorage.setItem('simnet_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  if (/mobile|iphone|android/i.test(ua)) return 'mobile';
  return 'desktop';
};

// Detect browser
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Other';
};

// Parse UTM params
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
  };
};

// Helper to invoke the tracking edge function
const trackViaEdge = async (payload: Record<string, unknown>) => {
  try {
    await supabase.functions.invoke('track-analytics', { body: payload });
  } catch (e) {
    // Silently fail — analytics should never break the user experience
    console.error('Analytics tracking error:', e);
  }
};

export const useAnalytics = () => {
  const location = useLocation();
  const visitorId = useRef(getVisitorId());
  const sessionId = useRef(getSessionId());
  const sessionStarted = useRef(false);
  const pageStartTime = useRef(Date.now());
  const maxScrollDepth = useRef(0);
  const previousPath = useRef<string | null>(null);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight > 0) {
        const depth = Math.round((window.scrollY / scrollHeight) * 100);
        maxScrollDepth.current = Math.max(maxScrollDepth.current, depth);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Start session on first load
  useEffect(() => {
    if (sessionStarted.current) return;
    sessionStarted.current = true;

    const utm = getUtmParams();

    trackViaEdge({
      action: 'session',
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      referrer: document.referrer || null,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      device_type: getDeviceType(),
      browser: getBrowser(),
      entry_page: location.pathname,
    }).then(() => {
      // Trigger new visitor alert
      supabase.functions.invoke('process-analytics-alerts', {
        body: {
          type: 'new_visitor',
          session_id: sessionId.current,
          visitor_id: visitorId.current,
          entry_page: location.pathname,
          referrer: document.referrer || null,
        }
      });
    });
  }, []);

  // Track page views
  useEffect(() => {
    pageStartTime.current = Date.now();
    maxScrollDepth.current = 0;

    trackViaEdge({
      action: 'pageview',
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      page_path: location.pathname,
      page_title: document.title,
      referrer_page: previousPath.current,
    });

    previousPath.current = location.pathname;
  }, [location.pathname]);

  // Track custom events
  const trackEvent = useCallback((
    eventType: string,
    options?: {
      category?: string;
      action?: string;
      label?: string;
      value?: Record<string, unknown>;
      elementId?: string;
      elementClass?: string;
      elementText?: string;
    }
  ) => {
    trackViaEdge({
      action: 'event',
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      event_type: eventType,
      event_category: options?.category || null,
      event_action: options?.action || null,
      event_label: options?.label || null,
      event_value: options?.value || {},
      page_path: location.pathname,
      element_id: options?.elementId || null,
      element_class: options?.elementClass || null,
      element_text: options?.elementText || null,
    }).then(() => {
      // Trigger event-based alerts
      if (eventType === 'form_submit') {
        supabase.functions.invoke('process-analytics-alerts', {
          body: {
            type: 'form_submit',
            session_id: sessionId.current,
            page_path: location.pathname,
            event_label: options?.label,
          }
        });
      }
    });
  }, [location.pathname]);

  // Track clicks
  const trackClick = useCallback((element: HTMLElement, label?: string) => {
    trackEvent('click', {
      category: 'interaction',
      action: 'click',
      label: label || element.innerText?.substring(0, 100),
      elementId: element.id,
      elementClass: element.className,
      elementText: element.innerText?.substring(0, 100),
    });
  }, [trackEvent]);

  // Track form submissions
  const trackFormSubmit = useCallback((formName: string, formData?: Record<string, unknown>) => {
    trackEvent('form_submit', {
      category: 'conversion',
      action: 'submit',
      label: formName,
      value: formData,
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackClick,
    trackFormSubmit,
    visitorId: visitorId.current,
    sessionId: sessionId.current,
  };
};

// Analytics context provider for global access
export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useAnalytics();
  return children;
};
